import { Messages, UserRoles } from '@/constants';
import { installTutorialGateway, isTutorialActive } from '@/features/Tutorial/tutorialGateway';
import axios, { type CancelTokenSource } from 'axios';
import { auth } from './firebaseService';

const READ_ONLY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function getJwtPayload(token?: string | null): Record<string, any> | null {
	if (!token) return null;

	const payload = token.split('.')[1];
	if (!payload) return null;

	try {
		const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
		const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
		return JSON.parse(atob(padded));
	} catch {
		return null;
	}
}

function isAllowedReadOnlySideEffect(method?: string, url?: string) {
	const normalizedMethod = (method ?? 'get').toUpperCase();
	if (normalizedMethod !== 'POST') return false;

	return (url ?? '').startsWith('/DocumentDownload/case/');
}

function isExternalPartnerReadOnlyRequest(
	token: string | null | undefined,
	method?: string,
	url?: string,
) {
	const role = getJwtPayload(token)?.Role;
	const normalizedMethod = (method ?? 'get').toUpperCase();
	return (
		role === UserRoles.ExternalPartner &&
		!READ_ONLY_METHODS.has(normalizedMethod) &&
		!isAllowedReadOnlySideEffect(method, url)
	);
}

/**
 * Recurso utilizado para fazer as chamadas de API da Atta.
 */
export class Http {
	/**
	 * Cria uma nova instancia do Axios
	 * @param baseUrl Url base do endpoint
	 */
	static create(baseUrl: string | undefined = import.meta.env.VITE_API_SERVICE) {
		const _http = axios.create({
			baseURL: baseUrl,
		});

		_http.defaults.headers.post['Content-Type'] = 'application/json';

		// O Interceptador irá inserir sempre o token de autorização durante a requisição

		let cachedToken: any = null;
		let cachedUid: string | null = null;
		let tokenExpiration: any = null;

		_http.interceptors.request.use(
			async (config) => {
				// Modo tutorial: a resposta vem de um roteiro e NADA sai para a rede. Fica
				// antes de tudo, inclusive do token, porque em treinamento não há por que
				// renovar credencial para uma chamada que não vai acontecer.
				if (isTutorialActive()) return installTutorialGateway(config);

				// O cache é do USUÁRIO, não só do relógio: o logout não recarrega a página, e sem comparar o
				// uid quem saía da imobiliária e entrava como admin seguia chamando a API como imobiliária
				// por até 1 hora (menu de admin, respostas de parceiro, 500 em /brokers).
				const uid = auth.currentUser?.uid ?? null;
				if (!cachedToken || Date.now() >= tokenExpiration || uid !== cachedUid) {
					const token = await auth.currentUser?.getIdToken(true);
					cachedToken = token;
					cachedUid = uid;
					tokenExpiration = Date.now() + 60 * 60 * 1000; // token válido por 1 hora
				}

				if (isExternalPartnerReadOnlyRequest(cachedToken, config.method, config.url)) {
					(window as any).$message?.warning('Parceiro Externo possui acesso somente leitura.');
					return Promise.reject(new Error('Parceiro Externo possui acesso somente leitura.'));
				}

				config.headers['Authorization'] = `Bearer ${cachedToken}`;
				return config;
			},
			(error) => Promise.reject(error),
		);

		_http.interceptors.response.use(
			(response) => response,
			(error) => {
				const status = error.response ? error.response.status : null;

				if (status === 401) {
					//router.push({ name: ROUTERS.LOGIN_SILENT_REFRESH })
					(window as any).$message?.error(Messages.ERROR_API_INVALID_CREDENTIALS, {
						duration: 0,
					});
				}

				if (axios.isAxiosError(error)) {
					const code = error.code;

					if (code == 'ERR_NETWORK')
						(window as any).$message?.error(Messages.ERROR_API_CONNECTION, {
							duration: 0,
							closable: true,
						});

					if (code == 'ERR_BAD_RESPONSE') {
						if (error.response) {
							const { ErrorMessage } = error.response.data;

							(window as any).$message?.error(ErrorMessage ?? Messages.ERROR_API_CONNECTION, {
								duration: 0,
								//render: AppErrorMessage,
								closable: true,
							});
						}
					}
				}

				return Promise.reject(error);
			},
		);

		return _http;
	}
}

export class HttpService {
	public static cancelTokens: { [key: string]: CancelTokenSource | null } = {};
	public static createCancelToken(key: string) {
		if (this.cancelTokens[key]) {
			this.cancelTokens[key]!.cancel(`Operation canceled due to new request for ${key}.`);
		}
		this.cancelTokens[key] = axios.CancelToken.source();
		return this.cancelTokens[key]!.token;
	}
}

export interface ApiResponse<T> {
	result: T;
	error: string;
	detail: Array<string>;
}

export default Http;
